import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { tenantService, Tenant, TenantUpdateData } from '../services/tenantService'
import { TenantForm } from '../components/TenantForm'
import './AdminTenants.css'

export function AdminTenants() {
  const queryClient = useQueryClient()
  const [showEditForm, setShowEditForm] = useState(false)
  const [editingTenant, setEditingTenant] = useState<Tenant | null>(null)
  const [filterStatus, setFilterStatus] = useState<string>('all')

  const { data: tenants, isLoading, error } = useQuery({
    queryKey: ['tenants'],
    queryFn: () => tenantService.getAll(),
  })

  const updateMutation = useMutation({
    mutationFn: ({ subdomain, data }: { subdomain: string; data: TenantUpdateData }) =>
      tenantService.update(subdomain, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenants'] })
      setShowEditForm(false)
      setEditingTenant(null)
      alert('Tenant updated successfully')
    },
    onError: (error: Error) => {
      alert(`Failed to update tenant: ${error.message || 'Unknown error'}`)
    },
  })

  const handleEdit = (tenant: Tenant) => {
    setEditingTenant(tenant)
    setShowEditForm(true)
  }

  const handleSubmit = async (data: TenantUpdateData) => {
    if (editingTenant) {
      await updateMutation.mutateAsync({ subdomain: editingTenant.subdomain, data })
    }
  }

  const handleCancel = () => {
    setShowEditForm(false)
    setEditingTenant(null)
  }

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'provisioned':
        return 'status-badge status-success'
      case 'provisioning':
        return 'status-badge status-warning'
      case 'pending':
        return 'status-badge status-info'
      case 'failed':
      case 'deletion_failed':
        return 'status-badge status-error'
      case 'deleting':
        return 'status-badge status-warning'
      case 'deleted':
        return 'status-badge status-neutral'
      default:
        return 'status-badge'
    }
  }

  const filteredTenants = tenants?.filter((tenant) => {
    if (filterStatus === 'all') return true
    return tenant.status === filterStatus
  })

  if (showEditForm && editingTenant) {
    return (
      <div className="admin-tenants" role="main">
        <div className="admin-tenants-header">
          <h1>Edit Tenant</h1>
        </div>
        <TenantForm
          initialData={editingTenant}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          isLoading={updateMutation.isPending}
        />
      </div>
    )
  }

  return (
    <div className="admin-tenants" role="main">
      <div className="admin-tenants-header">
        <div>
          <h1>Tenants</h1>
          <p className="admin-tenants-subtitle">Manage all clinic tenants</p>
        </div>
      </div>

      <div className="admin-tenants-filters">
        <label htmlFor="status-filter">Filter by status:</label>
        <select
          id="status-filter"
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="filter-select"
        >
          <option value="all">All Statuses</option>
          <option value="pending">Pending</option>
          <option value="provisioning">Provisioning</option>
          <option value="provisioned">Provisioned</option>
          <option value="failed">Failed</option>
          <option value="deleting">Deleting</option>
          <option value="deleted">Deleted</option>
        </select>
      </div>

      {isLoading && (
        <div className="loading" role="status" aria-live="polite">
          Loading tenants...
        </div>
      )}

      {error && (
        <div className="error" role="alert" aria-live="assertive">
          Error loading tenants: {error instanceof Error ? error.message : 'Unknown error'}
        </div>
      )}

      {!isLoading && !error && filteredTenants && (
        <div className="tenants-table-container">
          <table className="tenants-table">
            <thead>
              <tr>
                <th>Subdomain</th>
                <th>Clinic Name</th>
                <th>Contact Email</th>
                <th>Contact Person</th>
                <th>Phone</th>
                <th>Status</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredTenants.length === 0 ? (
                <tr>
                  <td colSpan={8} className="no-tenants">
                    No tenants found
                  </td>
                </tr>
              ) : (
                filteredTenants.map((tenant) => (
                  <tr key={tenant.subdomain}>
                    <td>
                      <strong>{tenant.subdomain}</strong>
                      <br />
                      <small className="subdomain-url">
                        {tenant.subdomain}.clnicl.ie
                      </small>
                    </td>
                    <td>{tenant.clinicName || 'N/A'}</td>
                    <td>{tenant.contactEmail || 'N/A'}</td>
                    <td>{tenant.contactPersonName || 'N/A'}</td>
                    <td>{tenant.phoneNumber || 'N/A'}</td>
                    <td>
                      <span className={getStatusBadgeClass(tenant.status)}>
                        {tenant.status}
                      </span>
                    </td>
                    <td>
                      {tenant.createdAt
                        ? new Date(tenant.createdAt).toLocaleDateString()
                        : 'N/A'}
                    </td>
                    <td>
                      <button
                        className="btn-secondary btn-sm"
                        onClick={() => handleEdit(tenant)}
                        disabled={tenant.status === 'deleted' || tenant.status === 'deleting'}
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      <div className="admin-tenants-info">
        <h2>Tenant Status Guide</h2>
        <div className="info-sections">
          <div className="info-section">
            <h3>Status Meanings</h3>
            <ul>
              <li>
                <span className="status-badge status-info">pending</span> - Registration received, provisioning not started
              </li>
              <li>
                <span className="status-badge status-warning">provisioning</span> - Infrastructure being deployed
              </li>
              <li>
                <span className="status-badge status-success">provisioned</span> - Stack deployed successfully
              </li>
              <li>
                <span className="status-badge status-error">failed</span> - Provisioning failed
              </li>
              <li>
                <span className="status-badge status-warning">deleting</span> - Deletion in progress
              </li>
              <li>
                <span className="status-badge status-neutral">deleted</span> - Successfully deleted
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  )
}
